/**
 * Workshop preview of the production workflow detail panel (editable mode).
 *
 * Mirrors the production WorkflowDetailsContent + WorkflowStepEditor
 * with local state so the designer can interact with the editing UI
 * without needing backend auth.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ─── Types (mirror production) ──────────────────────────────────────────────

type StepStatus = "pending" | "running" | "completed" | "failed" | "waiting_approval";

interface WorkflowStep {
  id: string;
  label: string;
  agent_key: string;
  tool_instruction: string;
  tool_params?: Record<string, string>;
  requires_approval: boolean;
  status: StepStatus;
  output?: string | null;
}

// ─── Mock Data ──────────────────────────────────────────────────────────────

const INITIAL_STEPS: WorkflowStep[] = [
  {
    id: "step-1",
    label: "Research newest Claude Code releases",
    agent_key: "Researcher",
    tool_instruction:
      "Search for the latest Claude Code CLI releases and changelog entries from the past 7 days. Focus on new features, breaking changes, and notable bug fixes.",
    tool_params: { days: "7", source: "github" },
    requires_approval: false,
    status: "completed",
  },
  {
    id: "step-2",
    label: "Find images and YouTube videos",
    agent_key: "Researcher",
    tool_instruction:
      "Find relevant screenshots, diagrams, and YouTube tutorial videos that demonstrate the new features found in step 1.",
    requires_approval: false,
    status: "pending",
  },
  {
    id: "step-3",
    label: "Build interactive HTML page",
    agent_key: "Coder",
    tool_instruction:
      "Create a single-page HTML document that presents the research findings with embedded media. Use a clean, modern design with dark theme support.",
    tool_params: { format: "html", theme: "dark" },
    requires_approval: true,
    status: "pending",
  },
  {
    id: "step-4",
    label: "Review and publish",
    agent_key: "Writer",
    tool_instruction: "Review the generated page for accuracy, grammar, and formatting. Fix any issues before publishing.",
    requires_approval: true,
    status: "failed",
    output: "Error: Could not access publishing API — authentication token expired.",
  },
];

// ─── Status dot colors ──────────────────────────────────────────────────────

const STATUS_DOT: Record<string, string> = {
  completed: "bg-emerald-400",
  running: "bg-blue-400 animate-pulse",
  failed: "bg-red-400",
  pending: "bg-muted",
  waiting: "bg-amber-400",
  waiting_approval: "bg-amber-400",
};

// ─── Step Editor (mirrors production) ───────────────────────────────────────

function StepEditor({
  step,
  index,
  disabled,
  onChange,
}: {
  step: WorkflowStep;
  index: number;
  disabled: boolean;
  onChange: (updated: WorkflowStep) => void;
}) {
  const params = step.tool_params ?? {};
  const paramEntries = Object.entries(params);

  return (
    <div
      className={cn(
        "rounded-lg border border-input-stroke bg-input-fill/30 p-3",
        step.status === "failed" && "border-red-400/30 bg-red-400/5",
      )}
      data-testid={`workshop-step-${index}`}
    >
      {/* Step header */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            STATUS_DOT[step.status] ?? "bg-muted",
          )}
        />
        <span className="flex-1 truncate text-xs font-medium text-primary">
          {step.label}
        </span>
        <Badge variant="outline" dot={false} className="text-[9px]">
          {step.agent_key}
        </Badge>
      </div>

      {/* Editable fields */}
      <div className="mt-3 flex flex-col gap-3 pl-4">
        {/* Instruction */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-medium text-secondary">
            Instruction
          </span>
          <textarea
            value={step.tool_instruction}
            onChange={(e) =>
              onChange({ ...step, tool_instruction: e.target.value })
            }
            disabled={disabled}
            rows={3}
            className={cn(
              "w-full resize-none rounded-lg border border-input-stroke bg-input-fill p-2 text-[11px] leading-relaxed text-primary shadow-input-default outline-none transition-shadow transition-colors",
              "placeholder:text-muted focus:border-accent focus:shadow-input-focus",
              disabled && "cursor-not-allowed opacity-50",
            )}
            data-testid={`workshop-step-instruction-${index}`}
          />
        </div>

        {/* Requires approval */}
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={step.requires_approval}
            onChange={(e) =>
              onChange({ ...step, requires_approval: e.target.checked })
            }
            disabled={disabled}
            className={cn(
              "h-3.5 w-3.5 rounded accent-accent",
              disabled && "cursor-not-allowed opacity-50",
            )}
            data-testid={`workshop-step-approval-${index}`}
          />
          <span className="text-[10px] text-secondary">Requires approval</span>
        </label>

        {/* Params */}
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-medium text-secondary">Params</span>
          {paramEntries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-1.5">
              <input
                type="text"
                value={key}
                onChange={(e) => {
                  const updated: Record<string, string> = {};
                  for (const [k, v] of Object.entries(params)) {
                    updated[k === key ? e.target.value : k] = v;
                  }
                  onChange({ ...step, tool_params: updated });
                }}
                disabled={disabled}
                placeholder="key"
                className={cn(
                  "w-20 shrink-0 rounded-md border border-input-stroke bg-input-fill px-2 py-1 text-[10px] text-primary shadow-input-default outline-none transition-shadow transition-colors",
                  "placeholder:text-muted focus:border-accent focus:shadow-input-focus",
                  disabled && "cursor-not-allowed opacity-50",
                )}
              />
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  onChange({
                    ...step,
                    tool_params: { ...params, [key]: e.target.value },
                  })
                }
                disabled={disabled}
                placeholder="value"
                className={cn(
                  "min-w-0 flex-1 rounded-md border border-input-stroke bg-input-fill px-2 py-1 text-[10px] text-primary shadow-input-default outline-none transition-shadow transition-colors",
                  "placeholder:text-muted focus:border-accent focus:shadow-input-focus",
                  disabled && "cursor-not-allowed opacity-50",
                )}
              />
              <button
                type="button"
                onClick={() => {
                  const updated = { ...params };
                  delete updated[key];
                  onChange({ ...step, tool_params: updated });
                }}
                disabled={disabled}
                className={cn(
                  "shrink-0 rounded-md p-1 text-muted transition-colors hover:bg-danger-muted hover:text-danger",
                  disabled && "cursor-not-allowed opacity-50",
                )}
                aria-label={`Remove param ${key}`}
              >
                <Icons.X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const newKey = `param${paramEntries.length + 1}`;
              onChange({ ...step, tool_params: { ...params, [newKey]: "" } });
            }}
            disabled={disabled}
            className="mt-0.5 h-6 w-full justify-start gap-1.5 px-1.5 text-[10px] text-muted hover:text-primary"
          >
            <Icons.Plus className="h-3 w-3" />
            Add param
          </Button>
        </div>

        {/* Runtime error output */}
        {step.status === "failed" && step.output && (
          <p className="mt-0.5 rounded-md bg-danger-muted px-2 py-1.5 text-[10px] leading-relaxed text-danger-light">
            {step.output}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Section label ──────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
      {children}
    </span>
  );
}

// ─── Main editable panel preview ────────────────────────────────────────────

export function WorkflowDetailsEditable() {
  const [steps, setSteps] = useState<WorkflowStep[]>(INITIAL_STEPS);
  const [isDirty, setIsDirty] = useState(false);

  function handleStepChange(index: number, updated: WorkflowStep) {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
    setIsDirty(true);
  }

  return (
    <div className="w-[340px] rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm">
      {/* Header — mirrors right panel details tab */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Info className="h-3.5 w-3.5 shrink-0 text-accent" />
        <span className="text-xs font-semibold text-primary">Details</span>
        <div className="flex-1" />
        <button
          type="button"
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-glass-stroke-subtle">
        <button type="button" className="flex-1 border-b-2 border-accent py-2 text-center text-[10px] font-medium text-primary">
          Details
        </button>
        <button type="button" className="flex-1 py-2 text-center text-[10px] font-medium text-muted">
          History
        </button>
        <button type="button" className="flex-1 py-2 text-center text-[10px] font-medium text-muted">
          Tasks
        </button>
      </div>

      {/* Title */}
      <div className="border-b border-glass-stroke-subtle px-3.5 py-3">
        <p className="text-sm font-medium text-primary">
          Claude Code Release Research
        </p>
      </div>

      {/* Trigger */}
      <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
        <SectionLabel>Trigger</SectionLabel>
        <div className="flex items-center gap-2">
          <Badge variant="outline" dot={false} className="text-[9px]">
            Manual
          </Badge>
        </div>
      </div>

      {/* Steps — editable */}
      <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
        <SectionLabel>Steps</SectionLabel>
        <div className="flex flex-col gap-2">
          {steps.map((step, idx) => (
            <StepEditor
              key={step.id}
              step={step}
              index={idx}
              disabled={false}
              onChange={(updated) => handleStepChange(idx, updated)}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 px-3.5 py-3">
        {isDirty && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsDirty(false)}
            className="w-full"
          >
            <Icons.Check className="h-3 w-3" />
            Save Changes
          </Button>
        )}
        <Button variant="default" size="sm" className="w-full">
          <Icons.Play className="h-3 w-3" />
          Run Workflow
        </Button>
        <Button variant="destructive" size="sm" className="w-full">
          <Icons.Trash2 className="h-3 w-3" />
          Delete
        </Button>
      </div>
    </div>
  );
}
